"use client";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuthContext } from "../auth/useAuthContext";
import {
  fetchSearchHistoryByUserId,
  postSearchHistory,
} from "@/api/search-history";
import { enqueueSnackbar } from "notistack";
import { searchPosts } from "@/api/posts";
import { getTrendingTerms } from "@/api/trending-terms";
import { useRouter, useSearchParams } from "next/navigation";

const GlobalContext = createContext();

export const GlobalContextProvider = ({ children }) => {
  const searchParams = useSearchParams();
  const searchQueryFromURL = searchParams.get("search");
  const router = useRouter();
  const { user } = useAuthContext();
  const [search, setSearch] = useState("");
  const [searchTabsLoader, setSearchTabsLoader] = useState(true);
  const [searchInFocus, setSearchInFocus] = useState("");
  const [searchHistory, setSearchHistory] = useState([]);
  const [showContentSkeleton, setShowContentSkeleton] = useState(true);
  const [showSearchingPostsLoader, setShowSearchingPostsLoader] =
    useState(true);
  const [frequentSearches, setFrequentSearches] = useState([]);
  const [searchRelatedTerms, setSearchRelatedTerms] = useState([]);
  const [trendingPostTerms, setTrendingPostTerms] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showDropdown, setShowDropdown] = useState(false);
  const [commentsLoader, setCommentsLoader] = useState(false);
  const relatedSearchRequestIdRef = useRef(0);

  const fetchUserSearchHistory = async (userId) => {
    try {
      const response = await fetchSearchHistoryByUserId(userId);
      if (response?.userSearchHistory) {
        setSearchHistory(response?.userSearchHistory);
      }

      if (response?.mostFrequestSearches) {
        setFrequentSearches(response?.mostFrequestSearches);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || error?.message || "Error",
        { variant: "error" }
      );
    }
  };

  const fetchRelatedSearches = async (searchQuery, requestId) => {
    try {
      const response = await searchPosts(searchQuery, {
        params: { suggestionsOnly: "true" },
        timeout: 2500,
      });

      if (requestId !== relatedSearchRequestIdRef.current) return;

      if (response?.searchSuggestions) {
        setSearchRelatedTerms(response?.searchSuggestions);
      }
    } catch (error) {
      if (requestId !== relatedSearchRequestIdRef.current) return;

      setSearchRelatedTerms([]);
      console.warn(
        "[search] related terms unavailable:",
        error?.response?.data?.message || error?.message || "Error"
      );
    }
  };

  const fetchTrendingTerms = async (searchQuery = "") => {
    try {
      const response = await getTrendingTerms(searchQuery);
      if (response?.error) {
        enqueueSnackbar(response?.error || "Error", {
          variant: "error",
        });
        return;
      }
      const cleanedResponse = response?.data?.filter((item) => item !== "");
      if (Array.isArray(cleanedResponse)) {
        setTrendingPostTerms(cleanedResponse);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || error?.message || "Error",
        { variant: "error" }
      );
    } finally {
      setSearchTabsLoader(false);
    }
  };

  const addToSearchHistory = async (searchQuery, userId) => {
    if (!searchQuery || searchQuery?.length === 0) return;
    try {
      const response = await postSearchHistory(searchQuery, userId);
      if (response.error) {
        enqueueSnackbar(response.error || "Error", { variant: "error" });
        return;
      }
      if (response && userId) {
        fetchUserSearchHistory(userId);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || error?.message || "Error",
        {
          variant: "error",
        }
      );
    }
  };

  const updateQueryParam = (key, value) => {
    const query = new URLSearchParams(
      !key?.trim() ? "" : window.location.search
    );

    if (key?.trim()) {
      const nextValue = typeof value === "string" ? value.trim() : value;
      if (nextValue === "" || nextValue === null || nextValue === undefined) {
        query.delete(key);
      } else {
        query.set(key, nextValue);
      }
    }

    const nextQuery = query.toString();
    const nextPath = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""
      }`;

    if (nextPath !== `${window.location.pathname}${window.location.search}`) {
      router.push(nextPath);
    }

    if (key === "search") {
      setShowSearchingPostsLoader(true);
      setShowContentSkeleton(true);
      setSearch(value);
    }
  };

  useEffect(() => {
    if (user && user?.userId) {
      fetchUserSearchHistory(user.userId);
    }
  }, [user]);

  useEffect(() => {
    const nextSearch = String(search || "").trim();
    relatedSearchRequestIdRef.current += 1;
    const requestId = relatedSearchRequestIdRef.current;

    if (nextSearch.length < 2) {
      setSearchRelatedTerms([]);
      return undefined;
    }

    const timeout = setTimeout(() => {
      fetchRelatedSearches(nextSearch, requestId);
    }, 250);

    return () => {
      clearTimeout(timeout);
    };
  }, [search]);

  useEffect(() => {
    setSearchTabsLoader(true);
    if (searchQueryFromURL) {
      setSearch(searchQueryFromURL);
      fetchTrendingTerms(searchQueryFromURL);
    } else {
      fetchTrendingTerms();
    }
  }, [searchQueryFromURL]);

  return (
    <GlobalContext.Provider
      value={{
        searchHistory,
        frequentSearches,
        search,
        setSearch,
        searchInFocus,
        setSearchInFocus,
        searchRelatedTerms,
        showSearchingPostsLoader,
        setShowSearchingPostsLoader,
        searchTabsLoader,
        trendingPostTerms,
        updateQueryParam,
        showContentSkeleton,
        setShowContentSkeleton,
        addToSearchHistory,
        fetchUserSearchHistory,
        filter,
        setFilter,
        showDropdown,
        setShowDropdown,
        commentsLoader,
        setCommentsLoader,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => {
  return useContext(GlobalContext);
};
